<template>
<div class="control-section sidebar-menu">
	<div class="max-h-screen">
		<div v-if="isPublic">
			<public-main-screen></public-main-screen>
		</div>
		<div v-else>
			<!-- sidebar element declaration -->
			<ejs-sidebar id="sidebar-menu" :width='width' :target='target' :dockSize='dockSize' :enableDock='enableDock'
			             type='Push' :enableGestures='false' v-show="showVertical" ref="sidebarInstance">
				<div class="flex flex-col h-screen">
					<div class="flex flex-col">
						<div class="flex flex-col items-start w-full pt-2 pl-1">
							<img v-bind:class="logo_css" :src="firmLogo">
						</div>
						<div class="icon-menu icon" id="menutoggler" v-on:click="toggleClick"></div>
					</div>
					<div class="grow">
						<ejs-menu id="main-menubar" :items='mainMenuItems' orientation = "Vertical" :select='selectMenu'></ejs-menu>
					</div>
					<div>
						<ejs-menu id="settings-menubar" :items='settingsMenuItems' orientation = "Vertical" :select='selectMenu'></ejs-menu>
					</div>
				</div>
			</ejs-sidebar>
			<!-- end of sidebar element -->
			<!-- main content declaration -->
			<div id="main" class="main-content">
				<div class="h-full flex flex-col md2:overflow-hidden">
					<div id="horizontal-header" v-show="!showVertical" class="bg-color-5 text-color-2 hover:text-color-2-hover">
						<div class="flex items-center justify-between h-16 pr-4">
							<img class="h-full my-0 py-0" :src="firmLogo">
							<div class="text-4xl icon-menu icon hover:cursor-pointer" @click="menuItemsVisible = !menuItemsVisible"></div>
						</div>
						<div v-show="menuItemsVisible" class="flex flex-row justify-items-start pt-2">
							<div class="w-full pr-1">
								<ejs-menu ref="menu" id="menu" :items='menuItems' orientation="Vertical" :select='selectMenu'></ejs-menu>
							</div>
						</div>
					</div>
					<div class="grow md2:overflow-auto text-color-text bg-color-5">
						<Notification :notification="notification" :closeAction="hideNotification"/>
						<router-view></router-view>
					</div>
					<div>
						<eny-footer></eny-footer>
					</div>
				</div>
			</div>
			<!--end of main content declaration -->
		</div>
	</div>
</div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { MenuComponent, SidebarComponent } from '@syncfusion/ej2-vue-navigations';
import PublicMainScreen from "./PublicArea/MainScreen.vue";
import Footer from "./PublicArea/Footer.vue";
import { inject } from '@vue/runtime-core';
import Notification from "./Base/Notification.vue";
import useNotification from "./hooks/useNotification";
import useUtils from './hooks/useUtils'
import datePickerUtils from '../syncfusion/datepicker';
import { isEmptyObject } from '@intlify/shared';

export default {
	name: 'MainMenu',
	components: {
		'ejs-sidebar': SidebarComponent,
		'ejs-menu': MenuComponent,
		'public-main-screen': PublicMainScreen,
		'eny-footer': Footer,
		Notification,
	},
	setup() {
		const { notification, hideNotification, showNotification } = useNotification();
		const { updateUserLocale, getCurUserId, setCurUserId } = useUtils();
		const { setLocale } = datePickerUtils();
		return {
			notification,
			hideNotification,
			showNotification,
			updateUserLocale,
			getCurUserId,
			setCurUserId,
			setLocale,
		}
	},
	created() {
		window.addEventListener("resize", this.resizeEventHandler);
		this.setLocale(this.$i18n.locale)
	},
	destroyed() {
		window.removeEventListener("resize", this.resizeEventHandler);
	},
	mounted() {
		this.adaptToMediaScreen();
		this.setCurRouteActive();
		if (!this.isPublic) {
			if (this.isCustomer) {
				const curUserId = this.getCurUserId();
				if (curUserId) {
					// Load the current user with the user id from locale storage.
					this.$store.dispatch('customer/loadWholeData', curUserId)
					this.$store.dispatch('user/loadCurrentUser', curUserId)
				} else {
					// Load customer requests.
					this.$store.dispatch('user/requests')
				}
			} else {
				// Load the current user.
				this.$store.dispatch('user/loadCurrentUser');
			}
		}
	},
	data: function() {
		return {
			width: "220px",
			target: '.main-content',
			dockSize: '50px',
			enableDock: true,
			isDocked: false,
			logo_css: "h-20",
			showVertical: true,
			mainMenuItems: this.showFullMenu ? this.getMenuItems(['Mainmenu']) : [],
			settingsMenuItems: this.showFullMenu ? this.getMenuItems(['Settings']) : this.getMenuItems([], ['logout']),
			menuItems: this.showFullMenu ? this.getMenuItems(['Mainmenu', 'Separator', 'Settings']) : this.getMenuItems([], ['logout']),
			menuItemsVisible: false,
			isPublic: !this.$keycloak || !this.$keycloak.ready || !this.$keycloak.keycloak.authenticated,
		}
	},
	methods: {
		...mapMutations('user', ['UPDATE_LOCALE_CHANGED']),
		resizeEventHandler(e) {
			this.adaptToMediaScreen();
		},
		adaptToMediaScreen() {
			const newShowVertical = !window.matchMedia('screen and (max-width:920px)').matches;
			if (newShowVertical && this.$refs.sidebarInstance) {
				if (this.isDocked) {
					this.$refs.sidebarInstance.hide();
				}
				else {
					this.$refs.sidebarInstance.show();
				}
			}
			if (this.showVertical != newShowVertical) {
				this.showVertical = newShowVertical;
				if (this.showVertical) {
					this.setCurRouteActive();
				}
				const marginLeft = this.showVertical ? (this.isDocked ? this.dockSize : this.width) : "0";
				const screens = document.querySelectorAll('.e-content-animation');
				for (const screen of screens) {
					screen.style.marginLeft = marginLeft;
				}
			}
			if (this.showVertical) {
				this.removeTransformStyle();
			}
		},
		toggleClick: function() {
			this.$refs.sidebarInstance.toggle();
			this.isDocked = !this.isDocked;
			this.logo_css = this.isDocked ? "h-10 w-10" : "h-20";
			this.removeTransformStyle();
		},
		removeTransformStyle() {
			const screens = document.querySelectorAll('.e-content-animation');
			for (const screen of screens) {
				screen.style.transform = '';
			}
		},
		selectMenu: function(args) {
			if (args.item.path == '/logout') {
				this.setCurUserId('')
				this.$keycloak.logoutFn();
			}
			else {
				if (args.item.name && (args.item.name==='offer' || args.item.name==='offer_eny')) {
					this.$store.dispatch('customer/reset');//reset offer data
				}
				this.$router.push(args.item.path);
				this.setMenuItemActive(args.element.getAttribute("aria-label"));
			}
		},
		setCurRouteActive() {
			const cur_path = '/' + this.window.location.pathname.split('/')[1];
			const curRoute = this.$router.options.routes.filter(route => {return route.path == cur_path});
			if (curRoute && curRoute[0]) {
				this.setMenuItemActive(curRoute[0].text);
			}
		},
		setMenuItemActive: function(ariaLabel) {
			const menuitems = document.querySelectorAll('.active-menu-item');
			for (const menuitem of menuitems) {
				menuitem.classList.remove('active-menu-item');
			}
			const selector = ".e-menu-item[aria-label='" + ariaLabel +"']";
			this.$nextTick(() => {
				const activeMenuItems = document.querySelectorAll(selector);
				for (const activeMenuItem of activeMenuItems) {
					activeMenuItem.classList.add('active-menu-item');
				}
			});
		},
		getMenuItems(types, allowedNames = []) {
			const cur_path = '/' + this.window.location.pathname.split('/')[1];
			const result = this.$router.options.routes.filter(route => {
				let isRouteAllowed =
					this.$keycloak.authenticated &&
					( (allowedNames.length > 0 && allowedNames.includes(route.name)) || types.includes(route.type) ) &&
					( this.isCustomer || route.name != 'requests' );
				if (isRouteAllowed) {
					if (route.name == 'offer') {
						isRouteAllowed = this.$keycloak.hasRealmRole('merchantAdmin') || this.$keycloak.hasRealmRole('merchant');
					}
					else if (route.name == 'offer_eny' || route.name == 'tests') {
						isRouteAllowed = this.$keycloak.hasRealmRole('admin');
					}
					else if (route.type == 'Separator') {
						route.separator = true;
					}
					route.text = route.key ? this.$t(route.key) : route.text;
					route.selected = (cur_path == route.path);
				}
				return isRouteAllowed;
			});
			return result;
		},
	},
	computed: {
		...mapGetters('user', ['user', 'firm', 'localeChanged', 'userRequests']),
		...mapGetters('customer', ['request']),
		isMerchant: function() {
			return this.$keycloak.hasRealmRole('merchantAdmin') || this.$keycloak.hasRealmRole('merchant');
		},
		isCustomer: function() {
			return this.$keycloak.hasRealmRole('customer');
		},
		showFullMenu: function() {
			return !this.isCustomer || this.request
		},
		firmLogo: function() {
			return this.firm.logo_src ? this.firm.logo_src : '/images/EnyLogo.png'
		}
	},
	watch: {
		menuItemsVisible(value, oldValue) {
			if (value) {
				this.setCurRouteActive();
			}
		},
		user(value, oldValue) {
			this.updateUserLocale(value.locale, this.$i18n)
			if (!isEmptyObject(value) && isEmptyObject(oldValue)) {
				if (this.isMerchant && !value['consented']) {
					// Redirect merchant to the privacy consented page if he had not consented yet.
					this.$router.push("/privacy_consent");
				}
			}
		},
		localeChanged(value) {
			if (value) {
				this.UPDATE_LOCALE_CHANGED(false);
				this.settingsMenuItems = this.getMenuItems(['Settings']);
				this.menuItems = this.getMenuItems(['Mainmenu', 'Separator', 'Settings']);
				this.mainMenuItems = this.getMenuItems(['Mainmenu']);
				this.setCurRouteActive();
			}
		},
		userRequests(newValue) {
			if (newValue && newValue.length == 1) {
				this.$store.dispatch('user/loadCurrentUser');
			} else {
				// Redirect customer to requests page in case there are more than one to choose one.
				this.$router.push("/requests");
			}
		}
	},
};

function externalFunction() {
	// log from external function
	const logger = inject('vuejs3-logger');
	logger.debug('log from function outside component.');
}
</script>
<style>
	@import "../../../node_modules/@syncfusion/ej2-base/styles/tailwind.css";
	@import "../../../node_modules/@syncfusion/ej2-vue-navigations/styles/tailwind.css";

	/* menu toggler */
	.sidebar-menu #menutoggler {
		@apply float-left pl-2 border-0 bg-color-5 cursor-pointer;
		font-size: 30px;
		float: left;
		line-height: 50px;
	}

	.sidebar-menu .right-header {
		height: 35px;
		padding: 7px;
		float: right;
	}

	.sidebar-menu .list {
		list-style: none;
		cursor: pointer;
		font-size: 16px;
		line-height: 35px;
	}

	.sidebar-menu .header-list {
		padding-left: 15px;
		margin: 0;
	}

	@media(max-width:500px) {
		.sidebar-menu .right-header.list.support,
		.sidebar-menu .right-header.list.tour {
			display: none;
		}
	}

	/* end of text input styles */
	/* end of header-section styles */

	/* content area styles */
	.sidebar-menu #main.main-content {
		height: 100vh;
		z-index: 1000;
	}
	/* end of content area styles */

	/* menu styles */
	/* horizontal-menu styles */
	.e-menu-wrapper .e-menu .e-menu-item .e-menu-icon, .e-menu-container .e-menu .e-menu-item .e-menu-icon,
	.sidebar-menu .e-menu li.e-menu-item {
		@apply font-bold text-lg !text-color-2 hover:!text-color-2-hover;
	}
	.sidebar-menu .e-menu li.e-menu-item:hover .e-menu-icon,
	.sidebar-menu .e-menu li.e-menu-item.active-menu-item,
	.sidebar-menu .e-menu li.e-menu-item.active-menu-item .e-menu-icon {
		@apply !text-color-2-hover bg-transparent;
	}

	.e-menu-wrapper ul .e-menu-item,
	.sidebar-menu .header-list .horizontal-menu .e-menu-item {
		@apply !text-color-2 hover:!text-color-2-hover;
		height: 35px;
		vertical-align: middle;
		font-size: 16px;
		line-height: 35px;
		width: 100%;
		padding: 0 0 0 3px;
	}

	.sidebar-menu .e-menu-item .e-caret {
		line-height: 35px;
	}

	.e-menu-wrapper {
		@apply bg-color-5 w-full;
	}
	/* end of horizontal-menu styles */

	.sidebar-menu .e-menu-item.e-focused:not(.active-menu-item):hover,
	.e-menu-wrapper .e-menu .e-menu-item.e-focused:not(.active-menu-item),
	.e-menu-wrapper .e-menu .e-menu-item.e-selected:not(.active-menu-item),
	.e-menu-container .e-menu .e-menu-item.e-focused:not(.active-menu-item),
	.e-menu-container .e-menu .e-menu-item.e-selected:not(.active-menu-item) {
		@apply bg-transparent;
	}

	.sidebar-menu .e-menu-item {
		@apply
			border-l-6 border-color-5
			hover:border-color-1;
	}
 /* end of vertical-menu styles */
 /* end of menu styles */

 /* Sidebar styles */
 /* docksidebar styles */
	.sidebar-menu .dock-menu .e-menu-wrapper ul .e-menu-item .e-caret,
	.sidebar-menu .e-menu-item .e-caret,
	.sidebar-menu .dock-menu .e-menu-wrapper ul .e-menu-item, .sidebar-menu .e-menu-item.e-focused {
		@apply text-white;
	}

	.sidebar-menu .dock-menu.e-close .e-menu-wrapper ul .e-menu-item {
		width: 50px;
	}

	.e-menu-wrapper ul .e-menu-item .e-menu-icon {
		margin: 0 2px 0 0;
		min-width: 30px;
	}

 .sidebar-menu #sidebar-menu.e-dock.e-close .e-menu-wrapper ul .e-menu-item .e-caret,
 .sidebar-menu #sidebar-menu.e-dock.e-close .main-menu-header {
		display: none;
 }

 .sidebar-menu #sidebar-menu.e-dock.e-close .e-menu-wrapper ul.e-vertical {
		min-width: 0px;
		width: 40px !important;
 	}

	.sidebar-menu #sidebar-menu.e-dock.e-close .e-menu-wrapper ul.e-menu {
		font-size: 0;
	}

	.sidebar-menu #sidebar-menu.e-dock.e-close .e-menu-item .e-menu-icon {
		font-size: 20px;
		padding: 0;
	}

	.sidebar-menu #sidebar-menu .e-menu-wrapper ul .e-menu-item .e-menu-icon,
	.e-menu-container ul .e-menu-item .e-menu-icon {
		font-size: 20px;
	}

	.sidebar-menu #sidebar-menu,
	.sidebar-menu #sidebar-menu ul,
	.sidebar-menu ul,
	.sidebar-menu .dock-menu .e-menu-wrapper,
	.sidebar-menu .dock-menu.e-menu-wrapper,
	.sidebar-menu .dock-menu.e-menu-wrapper ul>*,
	.sidebar-menu .dock-menu .e-menu-wrapper ul>*,
	.sidebar-menu .e-menu-wrapper .e-ul {
		@apply bg-color-5 text-color-2 hover:text-color-2-hover overflow-hidden w-full;
	}
	/* end of docksidebar styles */

	/* end of Sidebar styles */

	/* main-menu-header styles */
	.sidebar-menu #sidebar-menu .main-menu-header {
		@apply text-color-text;
		padding: 4px 0px 0px 18px;
	}
	/* main-menu-header styles end */
</style>
